import React, { lazy, Suspense } from "react";
import ReactDOM from "react-dom";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import "./scss/index.css";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import App from "./App";

import HttpService from "./services/HttpService";
import UserService from "./services/UserService";
import GuardedRoute from "./utils/GuardedRoute";
import AuthenticatedRoute from "./services/AuthenticatedRoute";
import LoaderBar from "./components/shared/LoaderBar";
import Calculators from "./components/introducer/BrokerCalculators";
import EditTurnaround from "./components/introducer/EditTurnaround";

const Home = lazy(() => import("./components/shared/home"));
const NotFoundPage = lazy(() => import("./components/shared/NotFoundPage"));
const NotAuthorizedPage = lazy(() =>
  import("./components/shared/NotAuthorizedPage")
);
const ManageUsers = lazy(() => import("./components/shared/ManageUsers"));
const Faqs = lazy(() => import("./components/connect/faqs"));
const Forms = lazy(() => import("./components/connect/forms"));
const ViewAccounts = lazy(() => import("./components/connect/ViewAccounts"));
const ContactDetails = lazy(() => import("./components/connect/my-details"));
const Documents = lazy(() => import("./components/connect/ClientDocuments"));
const IntroducerHome = lazy(() =>
  import("./components/introducer/IntroducerHome")
);
const Applications = lazy(() =>
  import("./components/introducer/IntroducerApplications")
);
const IntroducerApplicationInformation = lazy(() =>
  import("./components/introducer/ApplicationInformation")
);
const Portfolios = lazy(() => import("./components/introducer/Portfolios"));
const Scenarios = lazy(() => import("./components/introducer/Scenarios"));
const BrokerCalculators = lazy(() =>
  import("./components/introducer/BrokerCalculators")
);
const Resources = lazy(() => import("./components/introducer/Resources"));
const ViewPortfolio = lazy(() =>
  import("./components/introducer/ViewPortfolioAccount")
);
const LearningHub = lazy(() => import("./components/introducer/LearningHub"));
const LearningAcademy = lazy(() =>
  import("./components/introducer/LearningAcademy")
);
const SolicitorApplications = lazy(() =>
  import("./components/solicitor/SolicitorApplications")
);
const SolicitorApplicationInformation = lazy(() =>
  import("./components/solicitor/ApplicationInformation")
);
const SolicitorDischargeInformation = lazy(() =>
  import("./components/solicitor/Discharge/DischargeInformation")
);
const SolicitorDischarges = lazy(() =>
  import("./components/solicitor/SolicitorDischarges")
);
const ContactUs = lazy(() => import("./components/connect/ContactUs"));
const BenefitsRequest = lazy(() => import("./components/shared/Benefits"));
const ClientStatementPreferences = lazy(() => import("./components/connect/ClientStatementPreferences"));
const AccountSettings = lazy(() => import("./components/connect/AccountSettings"));
const BrokerMap = lazy(() => import("./components/introducer/BrokerMap"));
const BankDetails = lazy(() =>
  import("./components/connect/BankDetails")
);

const ChangePassword = lazy(() => import("./components/shared/ChangePassword"));
const NotificationsPage = lazy(() =>
  import("./components/connect/NotificationsPage")
);

const renderApp = () => {
  const title = process.env.REACT_APP_TITLE || "RedZed Connect";
  document.title = title;

  // Set favicon
  const favicon = document.getElementById("favicon");
  if (favicon) {
    favicon.href = process.env.REACT_APP_FAVICON || "/favicon.ico";
  } else {
    const link = document.createElement("link");
    link.id = "favicon";
    link.rel = "icon";
    link.href = process.env.REACT_APP_FAVICON || "/favicon.ico";
    document.head.appendChild(link);
  }
  return ReactDOM.render(
    <React.StrictMode>
      <Router>
        <Suspense fallback={<LoaderBar />}>
          <Routes>
            <Route path="/" element={<App />}>
              <Route
                path="/"
                element={
                  <Home />
                  // UserService._kc.realm.includes("Introducer_Connect") ? (
                  //   UserService.isClientServices() ? (
                  //     <ManageUsers />
                  //   ) : UserService.isRedZed() || UserService.isCalculator() ? (
                  //     <Calculators />
                  //   ) : UserService.isAggregator() ? (
                  //     <LearningHub />
                  //   ) : (
                  //     <IntroducerHome />
                  //   )
                  // ) : UserService.isSolicitor() ? (
                  //   <SolicitorApplications />
                  // ) : (
                  //   <Home />
                  // )
                }
              />

              <Route path="not-authorized" element={<NotAuthorizedPage />} />
              <Route path="*" element={<NotFoundPage />} />

              <Route
                path="applications"
                element={
                  UserService.isIntroducer() || UserService.isBDM() ? (
                    <AuthenticatedRoute>
                      <Applications />
                    </AuthenticatedRoute>
                  ) : (
                    UserService.isSolicitor() && <SolicitorApplications />
                  )
                }
              />
              <Route
                path="discharges"
                element={UserService.isSolicitor() && <SolicitorDischarges />}
              />

              <Route
                path="application/:loanNumber"
                element={
                  UserService.isIntroducer() || UserService.isBDM() ? (
                    <AuthenticatedRoute>
                      <IntroducerApplicationInformation />
                    </AuthenticatedRoute>
                  ) : (
                    UserService.isSolicitor() && (
                      <SolicitorApplicationInformation />
                    )
                  )
                }
              />
              <Route
                path="discharges/dischargeapplication/:loanNumber/:salesforceId"
                element={
                  UserService.isSolicitor() && <SolicitorDischargeInformation />
                }
              />

              {/* RedZed Connect Menu Items */}
              <Route
                path="forms"
                element={
                  <GuardedRoute isClient={true}>
                    <Forms />
                  </GuardedRoute>
                }
              />

              <Route path="documents" element={<Documents />} />

              <Route
                path="faqs"
                element={
                  <GuardedRoute isClient={true}>
                    <Faqs />
                  </GuardedRoute>
                }
              />
              <Route
                path="notifications"
                element={
                  UserService.hasRole("notification_admin") ? (
                    <GuardedRoute isClient={true}>
                      <NotificationsPage />
                    </GuardedRoute>
                  ) : (
                    <NotAuthorizedPage />
                  )
                }
              />
              <Route
                path="accounts"
                element={
                  <GuardedRoute isClient={true}>
                    <ViewAccounts />
                  </GuardedRoute>
                }
              />
                <Route
                path="bank-details"
                element={
                  <GuardedRoute isClient={true}>
                    <BankDetails />
                  </GuardedRoute>
                }
              />
              <Route path="benefits" element={<BenefitsRequest />} />
              <Route path="contact-us" element={<ContactUs />} />
              {/* Introducer Connect Menu Items */}

              <Route
                path="portfolios"
                element={
                  <AuthenticatedRoute>
                    <Portfolios />
                  </AuthenticatedRoute>
                }
              />

              <Route
                path="portfolio-account"
                element={
                  <AuthenticatedRoute>
                    {(UserService.isIntroducer() || UserService.isBDM()) && (
                      <ViewPortfolio />
                    )}
                  </AuthenticatedRoute>
                }
              />
              <Route path="scenarios" element={<Scenarios />} />
              <Route path="calculators" element={<BrokerCalculators />} />
              <Route
                path="resources"
                element={
                  <GuardedRoute isClient={false}>
                    <Resources />
                  </GuardedRoute>
                }
              />
              <Route
                path="broker-map"
                element={<BrokerMap />}
              />

              <Route
                path="learning-hub"
                element={
                  <GuardedRoute isClient={false}>
                    <LearningHub />
                  </GuardedRoute>
                }
              />
              <Route path="academy" element={<LearningAcademy />} />
              <Route path="edit-turnaround" element={<EditTurnaround />} />

              {/* Solicitor Connect Menu Items */}

              {/* Settings Dropdown Menu Items */}
              <Route
                path="my-details"
                element={
                  <GuardedRoute isClient={true}>
                    <ContactDetails />
                  </GuardedRoute>
                }
              />

              <Route path="manage-users" element={<ManageUsers />} />
              <Route path="edit-tunraround" element={<ManageUsers />} />
              <Route
                path="statement-preferences"
                element={
                  <GuardedRoute isClient={true}>
                    <ClientStatementPreferences />
                  </GuardedRoute>
                }
              />
              <Route
                path="account-settings"
                element={
                  <GuardedRoute isClient={true}>
                    <AccountSettings />
                  </GuardedRoute>
                }
              />
              {/* <Route path="edit-user" element={
                <GuardedRoute isClient={false}>
                  <EditUser />
                </GuardedRoute>
              }/> */}
              <Route path="change-password" element={<ChangePassword />} />
            </Route>
          </Routes>
        </Suspense>
      </Router>
    </React.StrictMode>,
    document.getElementById("root")
  );
};

UserService.initKeycloak(renderApp);
HttpService.configure();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
